import React, { ChangeEvent, useState } from 'react';
import { DataTable } from '@/components/shared';
import { List } from '@refinedev/antd';
import { endPoints } from '@/api/configs';
import { useList } from '@/hooks/useList';
import { useTranslation } from '@refinedev/core';
import { FileImageOutlined, ReloadOutlined } from '@ant-design/icons';
import { getColumns } from './columns';
import { PostListRes, Type } from '@/api/types/getPostList';
import { Form, Input, Select, Button, message } from 'antd';
import useAPI from '@/api/useApi';
import { useProfileStore } from '@/core';

export const PostList = () => {
  const { translate } = useTranslation();
  const { userData } = useProfileStore();
  const adminType = userData?.account?.admin?.admin_type;
  const { usePost } = useAPI();
  const [publishId, setPublishId] = useState<number | null>(null);
  const [form] = Form.useForm();
  const {
    data,
    isLoading,
    page,
    refetch,
    setPage,
    isRefetching,
    setSearch,
    setFilters,
    resolvedTotal,
  } = useList<{
    data: PostListRes[];
  }>({
    endpoint: endPoints.post(),
  });
  const { mutateAsync: togglePublish } = usePost(
    endPoints.togglePublishPost(String(publishId))
  );
  const handlePublish = () => {
    togglePublish(
      {},
      {
        onSuccess: () => {
          message.success(translate('listing-posts.success.toggle-publish'));
          refetch();
        },
        onError: (error) => {
          message.error(error.message);
        },
      }
    );
  };
  const handlePublishId = (id: number) => {
    setPublishId(id);
  };
  const columns = getColumns(
    translate,
    handlePublishId,
    handlePublish,
    adminType
  );
  const onSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setPage(1);
    setSearch(e.target.value);
  };
  const handleSelectRole = (value: Type) => {
    setPage(1);
    setFilters((prev) => ({
      ...prev,
      type: value,
    }));
  };
  const handleSelectIsPublished = (value: boolean) => {
    setPage(1);
    setFilters((prev) => ({
      ...prev,
      is_published: value,
    }));
  };
  const handleSelectIsFeatured = (value: boolean) => {
    setPage(1);
    setFilters((prev) => ({
      ...prev,
      is_featured: value,
    }));
  };
  const handleClearFilters = () => {
    form.resetFields();
    setFilters({
      search: undefined,
      type: undefined,
      is_published: undefined,
      is_featured: undefined,
    });
  };
  return (
    <List
      title={
        <div className="flex gap-x-3">
          <FileImageOutlined />
          <h1>{translate('listing-posts.title')}</h1>
        </div>
      }
    >
      <Form form={form} layout="vertical">
        <div className="flex flex-wrap gap-x-3">
          <Form.Item name="search" className="w-full sm:w-1/6 min-w-[200px]">
            <Input
              placeholder={translate('search-admin.fields.search')}
              onChange={onSearch}
            />
          </Form.Item>
          <Form.Item
            name="admin_type"
            className="w-full sm:w-1/6 min-w-[200px]"
          >
            <Select
              placeholder={translate('listing-posts.type')}
              onChange={handleSelectRole}
              options={Object.values(Type).map((type) => ({
                label: translate(`listing-posts.type.${type}`),
                value: type,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="is_published"
            className="w-full sm:w-1/6 min-w-[200px]"
          >
            <Select
              placeholder={translate('listing-posts.is_published')}
              onChange={handleSelectIsPublished}
              options={[
                { label: translate('listing-posts.published'), value: true },
                { label: translate('listing-posts.unpublished'), value: false },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="is_featured"
            className="w-full sm:w-1/6 min-w-[200px]"
          >
            <Select
              placeholder={translate('listing-posts.is_featured')}
              onChange={handleSelectIsFeatured}
              options={[
                { label: translate('listing-posts.featured'), value: true },
                {
                  label: translate('listing-posts.not_featured'),
                  value: false,
                },
              ]}
            />
          </Form.Item>
          <Button
            className="mb-2 sm:mb-0"
            type="primary"
            onClick={handleClearFilters}
            icon={<ReloadOutlined />}
          />
        </div>
      </Form>
      <DataTable
        pageSize={10}
        currentPage={page}
        onPageChange={setPage}
        columns={columns}
        loading={isLoading || isRefetching}
        data={{
          data: data?.data.data || [],
          headers: { 'x-total-count': resolvedTotal },
        }}
      />
    </List>
  );
};
